import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Building2, Home, User, Calendar, Package, FileText } from 'lucide-react';

const DetalleSolicitudModal = ({ isOpen, onClose, solicitud }) => {
  if (!solicitud) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <FileText className="h-6 w-6 text-blue-600" />
            Detalle de Solicitud
          </DialogTitle>
          <DialogDescription>
            Información completa de la solicitud de insumos
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Información General */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">Fecha de Solicitud</label>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-sm">
                  {new Date(solicitud.createdAt).toLocaleString('es-ES', {
                    dateStyle: 'full',
                    timeStyle: 'short'
                  })}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">Estado</label>
              <Badge className="bg-yellow-100 text-yellow-800">
                {solicitud.estado}
              </Badge>
            </div>
          </div>

          {/* Solicitante */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-500">Solicitado por</label>
            <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg">
              <User className="h-5 w-5 text-purple-600" />
              <span className="font-medium">{solicitud.creator?.nombre}</span>
            </div>
          </div>

          {/* Ubicación */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">Edificio</label>
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-medium">{solicitud.edificio?.nombre}</div>
                  <div className="text-xs text-gray-500">{solicitud.edificio?.ciudad}</div>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">Activo</label>
              <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg">
                <Home className="h-5 w-5 text-green-600" />
                <div>
                  <div className="font-medium">{solicitud.hospedaje?.nombre || solicitud.nombrePropiedad}</div>
                  <div className="text-xs text-gray-500 capitalize">{solicitud.hospedaje?.tipo}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Items Solicitados */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-500">Items Solicitados</label>
              <Badge variant="outline" className="font-semibold">
                <Package className="h-3 w-3 mr-1" />
                {solicitud.total_solicitado} items
              </Badge>
            </div>

            <div className="border rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Insumo</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Observación</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {solicitud.items?.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{item.nombre}</div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline" className="text-xs">
                          {item.tipo}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Badge className="bg-blue-100 text-blue-800 font-semibold">
                          {item.cantidad}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.observacion || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Observaciones */}
          {solicitud.observaciones && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-500">Observaciones</label>
              <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
                {solicitud.observaciones}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DetalleSolicitudModal;
